import { AppConfig } from "../utils/AppConfig.js";
import { ApiService } from "../utils/ApiService.js";
import { parsearReglas } from "./sucursalesReglas.js";

// Sesión contra LCApii: access token de 15 min, refresh rotativo de 10 días y
// login central en otro dominio.
//
// Se aparta del patrón estándar en un punto: la ventana de gracia offline. La
// app se usa en sucursales con conexión inestable y los códigos se calculan en
// el navegador, así que una caída de internet no debería impedir trabajar. La
// distinción importante es entre no poder preguntar y que te digan que no:
//
//   - falla de red (sin respuesta)  → se sigue con la última sesión conocida,
//     hasta AppConfig.sesion.graciaOfflineMs desde la última validación exitosa.
//   - respuesta 401                 → al login, sin gracia. Un rechazo explícito
//     nunca se interpreta como caída: si al usuario le dieron de baja, deja de
//     operar.

const ACCESS_KEY = "lcapii_access";
const REFRESH_KEY = "lcapii_refresh";
const EXP_KEY = "lcapii_access_exp";
const VALIDACION_KEY = "lcapii_ultima_validacion";
const SESION_KEY = "sesion";
const PERMISOS_KEY = "permisos";

const url = () => AppConfig.lcapii.url.replace(/\/$/, "");
const sid = () => AppConfig.lcapii.sid;

let refreshEnVuelo = null;

// Resultado de la última validación del arranque: "ok" cuando LCApii contestó,
// "gracia" cuando se sigue con datos guardados por falta de red.
let estadoSesion = "sin-sesion";

const leerJson = (clave, porDefecto) => {
  try {
    const raw = localStorage.getItem(clave);
    return raw ? JSON.parse(raw) : porDefecto;
  } catch {
    return porDefecto;
  }
};

/** Un error sin status vino de la red; con status, lo mandó el servidor. */
const esFallaDeRed = (error) => !error?.status;

export const LcapiiService = {
  /**
   * El login central devuelve los tokens en el hash de la URL (no en query, para
   * que no queden en los logs del servidor). Hay que capturarlos antes de que el
   * router lea el hash como si fuera una ruta.
   */
  capturarTokensDelHash() {
    const hash = window.location.hash.slice(1);
    if (!hash.includes("access=")) return false;

    const params = new URLSearchParams(hash);
    const access = params.get("access");
    const refresh = params.get("refresh");
    if (!access || !refresh) return false;

    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    const exp = parseInt(params.get("exp") || "0", 10);
    localStorage.setItem(EXP_KEY, String(exp || Date.now() + 15 * 60 * 1000));

    // Se limpia el hash para no dejar los tokens en la barra de direcciones ni
    // en el historial del navegador.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return true;
  },

  redirigirALogin() {
    const destino = `${window.location.origin}${window.location.pathname}`;
    window.location.href = `${url()}/auth/login.html?redirect=${encodeURIComponent(destino)}`;
  },

  urlCambiarClave() {
    return `${url()}/auth/chPass.html`;
  },

  hayTokens() {
    return !!localStorage.getItem(ACCESS_KEY);
  },

  async getAccessToken() {
    if (AppConfig.lcapii.skipLogin) return "dev";
    if (!localStorage.getItem(ACCESS_KEY)) return null;

    const exp = parseInt(localStorage.getItem(EXP_KEY) || "0", 10);
    // Se renueva 30 s antes de que expire, para que no venza en pleno request.
    if (Date.now() >= exp - 30_000) {
      const ok = await this.refrescar();
      if (!ok) return null;
    }
    return localStorage.getItem(ACCESS_KEY);
  },

  /** Una sola rotación a la vez: dos llamadas en paralelo invalidarían la familia. */
  async refrescar() {
    if (refreshEnVuelo) return refreshEnVuelo;

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return false;

    refreshEnVuelo = (async () => {
      try {
        const d = await ApiService.makeRequest(
          "POST",
          `${url()}/usuario/refresh`,
          { RefreshToken: refreshToken },
          null,
          { dedupe: false },
        );
        localStorage.setItem(ACCESS_KEY, d.accessToken);
        localStorage.setItem(REFRESH_KEY, d.refreshToken);
        localStorage.setItem(EXP_KEY, String(Date.now() + d.expiresIn * 1000));
        return true;
      } catch (error) {
        // Un 401 acá significa que la familia fue revocada (reuso detectado):
        // no se reintenta, se vuelve a autenticar.
        if (!esFallaDeRed(error)) this.limpiar();
        return false;
      } finally {
        refreshEnVuelo = null;
      }
    })();

    return refreshEnVuelo;
  },

  /**
   * Valida contra LCApii y guarda los datos de sesión. Devuelve el motivo del
   * resultado para que el arranque decida: "ok" | "gracia" | "sin-sesion".
   */
  async establecerSesion() {
    estadoSesion = await this._validar();
    return estadoSesion;
  },

  /** Estado de la última validación, para avisar en pantalla cuando se opera en gracia. */
  hayValidacionFresca() {
    return estadoSesion === "ok";
  },

  async _validar() {
    if (AppConfig.lcapii.skipLogin) {
      localStorage.setItem(
        SESION_KEY,
        JSON.stringify({
          nombres: "Dev User",
          urlFoto: "",
          sucursales: [0],
          derivacionOrigen: [0],
          derivacionDestino: [0],
        }),
      );
      return "ok";
    }

    if (!this.hayTokens()) return "sin-sesion";

    const token = await this.getAccessToken();
    if (!token) return this.puedeSeguirEnGracia() ? "gracia" : "sin-sesion";

    try {
      const data = await ApiService.makeRequest(
        "GET",
        `${url()}/usuario/validar?sistema=${sid()}`,
        null,
        token,
        { dedupe: false },
      );

      const datosUsuario = (() => {
        try {
          return JSON.parse(data.datosUsuario || data.DatosUsuario || "{}");
        } catch {
          return {};
        }
      })();

      // Reglas del sistema, no del usuario: vienen en datosSistema. La
      // exclusión se aplica antes que cualquier lista de datosUsuario.
      const reglas = parsearReglas(data.datosSistema || data.DatosSistema);

      localStorage.setItem(
        SESION_KEY,
        JSON.stringify({
          nombres: data.nombres || data.Nombres || "",
          urlFoto: data.urlFoto || data.UrlFoto || "",
          // Sin sucursales cargadas la sección no opera, en vez de operar contra
          // todas: es un permiso, y ante la duda no se concede.
          sucursales: datosUsuario.sucursales || [],
          derivacionOrigen: datosUsuario.derivacion_ori || [],
          derivacionDestino: datosUsuario.derivacion_des || [],
          sucursalesExcluidas: reglas.excluidas,
          sucursalesAlias: reglas.alias,
        }),
      );
      localStorage.setItem(VALIDACION_KEY, String(Date.now()));
      return "ok";
    } catch (error) {
      if (esFallaDeRed(error) && this.puedeSeguirEnGracia()) return "gracia";
      if (!esFallaDeRed(error)) this.limpiar();
      return "sin-sesion";
    }
  },

  /** Hay datos de una validación previa y todavía está dentro de la ventana. */
  puedeSeguirEnGracia() {
    const ultima = parseInt(localStorage.getItem(VALIDACION_KEY) || "0", 10);
    if (!ultima) return false;
    if (!localStorage.getItem(SESION_KEY)) return false;
    return Date.now() - ultima < AppConfig.sesion.graciaOfflineMs;
  },

  async cargarPermisos() {
    if (AppConfig.lcapii.skipLogin) return null;

    const token = await this.getAccessToken();
    if (!token) return this.permisos();

    try {
      const lista = await ApiService.makeRequest(
        "GET",
        `${url()}/usuario/PermisosDeSistema/${sid()}`,
        null,
        token,
        { dedupe: false },
      );
      const asignados = (Array.isArray(lista) ? lista : [])
        .filter((p) => p.asignado)
        .map((p) => p.id);
      localStorage.setItem(PERMISOS_KEY, JSON.stringify(asignados));
      return asignados;
    } catch {
      // Sin red se trabaja con los permisos de la última sincronización.
      return this.permisos();
    }
  },

  permisos() {
    return leerJson(PERMISOS_KEY, []);
  },

  tienePermiso(pid) {
    if (AppConfig.lcapii.skipLogin) return true;
    return this.permisos().includes(pid);
  },

  sesion() {
    return leerJson(SESION_KEY, {
      nombres: "",
      urlFoto: "",
      sucursales: [],
      derivacionOrigen: [],
      derivacionDestino: [],
      sucursalesExcluidas: [],
      sucursalesAlias: {},
    });
  },

  limpiar() {
    [ACCESS_KEY, REFRESH_KEY, EXP_KEY, VALIDACION_KEY, SESION_KEY, PERMISOS_KEY].forEach((k) =>
      localStorage.removeItem(k),
    );
  },

  async logout() {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (refreshToken) {
      try {
        await ApiService.makeRequest("POST", `${url()}/usuario/logout`, { RefreshToken: refreshToken }, null, {
          dedupe: false,
        });
      } catch {
        // el logout es idempotente del lado del servidor; si no se pudo avisar,
        // igual se limpia la sesión local
      }
    }
    this.limpiar();
    this.redirigirALogin();
  },
};
